import React from "react";
import { navigate } from "gatsby";

import RiotBarLoader from "@riot-private/riotbar-react";

import { ModalTypes, ProductGame } from "@/@types/common";
import { useModal } from "@/providers/modal";
import { ActionType, useLoginActions } from "@/providers/useLoginActions";
import { useRiotBar } from "@/providers/useRiotBar";
import { useRSOAuth } from "@/providers/useRSOAuth";
import { setRiotBarAuthState } from "@/utils/riotbar";
import { useI18n } from "@/utils/rioti18n";
import { CustomFooterStyles, RiotBarContainer, RiotbarHolder } from "./style";
import { BalancePortal } from "./Plugins/Balance";
import logger from "@/utils/logger";
import { useGame } from "@/providers/useGame";
import { useXSSOAuth } from "@/providers/useXSSOAuth";
import { purchaseVcBundle } from "@/data/ordersV3";

type RiotBarProps = {
  theme?: string;
  footerStyle?: CustomFooterStyles;
  game?: ProductGame;
};

export const RiotBarFooter = () => <footer data-testid="riotbar-footer-container" id="riotbar-footer-container" />;

export const refreshFooter = () => {
  // In certain scenarios, Riotbar may append the footer directly to the body instead of the intended
  // "riotbar-container" element. This ensures the footer is re-rendered
  // in the correct container and avoids duplicates.
  const footerContainer = document.getElementById("riotbar-container");
  if (footerContainer?.hasChildNodes()) {
    window.RiotBar?.render();
  }
};

const NAVBAR_LINKS: { [key: string]: CallableFunction } = {
  // Links which are defined with Programmatic Action in Riotbar Contentstack
  redeem: (locale: string) => () => navigate(`/${locale}/redeem/`),
  topup:
    (
      locale: string,
      isLoggedIn: boolean,
      updateModal: CallableFunction,
      logout: CallableFunction,
      saveLoginAction: CallableFunction,
      login: CallableFunction,
    ) =>
    async () => {
      const gameId: ProductGame = useGame();
      /* TOP UP = Purchase VCC|RP
       * This function invokes in two ways:
       * - After player clicked to the navigation (Riotbar)
       * - Or they already clicked but redirected to the logged-in and then come back to the page
       */
      // Check if player is already logged-in
      // If not, redirect them, and trigger another attempt
      if (!isLoggedIn) {
        await saveLoginAction(ActionType.PURCHASE_VC_BUNDLE);
        login();
        return;
      }
      const modalType = await purchaseVcBundle(locale, gameId); // TODO: Make the button disabled during the API call.
      if (!modalType) {
        // Everything was fine
        return;
      }

      if (modalType == ModalTypes.UNAUTHORIZED) {
        logout();
      } else if (modalType == ModalTypes.SERVER_ERROR) {
        updateModal(modalType);
      } else {
        logger("purchaseVcBundle returned an unhandled status");
        updateModal(ModalTypes.SERVER_ERROR);
      }
    },
};

export const getRiotbarProductId = () => {
  if (typeof window !== "undefined") {
    const { pathname } = window.location;
    const paths = pathname.split("/");

    if (paths.indexOf("redeem") > -1) {
      return `shop-riotgames-com-riot`;
    }
  }

  // TODO: Valorant / multigame?
  return `directcontent-lol`;
};

export const RiotBar = ({ footerStyle, game }: RiotBarProps) => {
  const { language: locale } = useI18n();
  const { riotBarReady, onRiotBarLoad } = useRiotBar();
  const { rsoReady, session, user: { game_name } = {}, hookLoginFunctions: hookLoginRSOFunctions } = useRSOAuth();
  const { isLoggedIn, login, logout, hookLoginFunctions: hookLoginXSSOFunctions } = useXSSOAuth();
  const { saveLoginAction } = useLoginActions();
  const { updateModal } = useModal();
  const riotBarProductId = getRiotbarProductId();
  React.useEffect(() => {
    if (!riotBarReady || !rsoReady || typeof window === "undefined") {
      return;
    }
    // Iterate through the Programmatic Action links in Riotbar Contentstack and init them
    Object.keys(NAVBAR_LINKS).forEach(
      (key) =>
        (window.RiotBar.navigation[key] = NAVBAR_LINKS[key](
          locale,
          isLoggedIn,
          updateModal,
          logout,
          saveLoginAction,
          login,
        )),
    );
    if (riotBarProductId === "shop-riotgames-com-riot") {
      hookLoginRSOFunctions();
      setRiotBarAuthState(!!session, game_name);
    } else {
      hookLoginXSSOFunctions();
    }
  }, [rsoReady, session, game_name, riotBarReady, riotBarProductId, isLoggedIn]);

  const riotbarEnv = process.env.GATSBY_RIOTBAR_ENV || "live";

  return (
    <>
      <RiotBarContainer footerStyle={footerStyle} />
      <RiotbarHolder />
      {/* https://github.com/riot-private/wwpub-packages/blob/d5f16bd0af8ca9eb51dba2231ee5906f3f6ea02f/libraries/riotbar-react/src/loader.tsx#L30 */}
      <RiotBarLoader
        product={getRiotbarProductId()}
        environment={riotbarEnv}
        version={"latest"}
        locale={locale}
        onLoad={() => onRiotBarLoad()}
      />
      {riotBarReady && (
        <BalancePortal
          game={game}
          desktopContainer={".riotbar-account-container"}
          mobileContainer={".riotbar-mobile-menu-touchpoints"}
        />
      )}
    </>
  );
};
